import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Plus, Power, ArrowUp, ArrowDown } from 'lucide-react';
import { Button, Card, Input } from '../components';
import api from '../services/api';
import type { AcademyCategorie } from '../services/medewerkerApi';

/**
 * Admin-beheer van de Academy-subcategorieën (de "pills" in de catalogus van
 * het medewerkersportaal). Server: /api/admin/academy-subcategorieen
 * (tabel academy_subcategories, migratie 127).
 *
 * Verwijderen bestaat niet: deactiveren verbergt de pill in de catalogus,
 * gekoppelde trainingen blijven gekoppeld.
 */

interface SubcategorieRow {
  id: number;
  hoofdgroep: AcademyCategorie;
  naam: string;
  slug: string;
  emoji: string | null;
  omschrijving: string | null;
  volgorde: number;
  is_active: boolean;
  aantal_trainingen: number;
}

interface SubcategorieenPayload {
  subcategorieen: SubcategorieRow[];
  hoofdgroepen: AcademyCategorie[];
}

type Patch = Partial<Pick<SubcategorieRow, 'naam' | 'emoji' | 'volgorde' | 'is_active' | 'hoofdgroep'>>;

const HOOFDGROEPEN: AcademyCategorie[] = ['MENTAL', 'PHYSICAL', 'FINANCIAL', 'GROWTH'];
const HOOFDGROEP_DEFAULTS: Record<AcademyCategorie, { label: string; emoji: string }> = {
  MENTAL: { label: 'Mentale Kracht & Veerkracht', emoji: '🧠' },
  PHYSICAL: { label: 'Fysieke Fitheid & Lifestyle', emoji: '🏃' },
  FINANCIAL: { label: 'Financieel Welzijn', emoji: '🪙' },
  GROWTH: { label: 'Groei, Passie & Ambitie', emoji: '📈' },
};

const QUERY_KEY = ['admin-academy-subcategorieen'];

const AdminAcademySubcategorieenPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();

  const [hoofdgroep, setHoofdgroep] = useState<AcademyCategorie>('MENTAL');
  const [naam, setNaam] = useState('');
  const [emoji, setEmoji] = useState('');
  const [slug, setSlug] = useState('');

  const tr = (key: string, defaultValue: string, opts: Record<string, unknown> = {}) =>
    t(`adminAcademySubcategorieen.${key}`, { defaultValue, ...opts });
  const groepLabel = (h: AcademyCategorie) => tr(`group_${h}`, HOOFDGROEP_DEFAULTS[h].label);

  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await api.get('/admin/academy-subcategorieen');
      return res.data.data as SubcategorieenPayload;
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  const errorMessage = (error: any) =>
    error?.response?.data?.errors?.[0] || error?.response?.data?.message || tr('toastError', 'Er ging iets mis');

  const addMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/academy-subcategorieen', {
        hoofdgroep,
        naam,
        emoji: emoji.trim() || null,
        ...(slug.trim() ? { slug: slug.trim() } : {}),
      }),
    onSuccess: () => {
      toast.success(tr('toastAdded', 'Subcategorie toegevoegd'));
      setNaam('');
      setEmoji('');
      setSlug('');
      invalidate();
    },
    onError: (error: any) => toast.error(errorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Patch }) => api.patch(`/admin/academy-subcategorieen/${id}`, patch),
    onSuccess: () => {
      toast.success(tr('toastUpdated', 'Subcategorie bijgewerkt'));
      invalidate();
    },
    onError: (error: any) => {
      toast.error(errorMessage(error));
      invalidate();
    },
  });

  // Volgorde: de groep opnieuw nummeren (10, 20, 30…) en alleen gewijzigde rijen patchen.
  const reorderMutation = useMutation({
    mutationFn: async (items: SubcategorieRow[]) => {
      const wijzigingen = items
        .map((row, i) => ({ row, volgorde: (i + 1) * 10 }))
        .filter(({ row, volgorde }) => row.volgorde !== volgorde);
      for (const { row, volgorde } of wijzigingen) {
        await api.patch(`/admin/academy-subcategorieen/${row.id}`, { volgorde });
      }
    },
    onSuccess: () => {
      toast.success(tr('toastReordered', 'Volgorde bijgewerkt'));
      invalidate();
    },
    onError: (error: any) => {
      toast.error(errorMessage(error));
      invalidate();
    },
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!naam.trim()) return;
    addMutation.mutate();
  };

  const verplaats = (groep: SubcategorieRow[], index: number, richting: -1 | 1) => {
    const doel = index + richting;
    if (doel < 0 || doel >= groep.length) return;
    const nieuw = [...groep];
    [nieuw[index], nieuw[doel]] = [nieuw[doel], nieuw[index]];
    reorderMutation.mutate(nieuw);
  };

  const rows = data?.subcategorieen ?? [];
  const busy = addMutation.isPending || updateMutation.isPending || reorderMutation.isPending;
  const perGroep = HOOFDGROEPEN.map((h) => ({
    hoofdgroep: h,
    items: rows.filter((r) => r.hoofdgroep === h).sort((a, b) => a.volgorde - b.volgorde || a.naam.localeCompare(b.naam)),
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> {tr('back', 'Terug naar dashboard')}
        </button>

        <h1 className="text-2xl font-bold text-gray-900">{tr('title', 'Academy-subcategorieën')}</h1>
        <p className="text-sm text-gray-600 mt-1 mb-6">
          {tr(
            'subtitle',
            'De pills in de Academy-catalogus van het medewerkersportaal. Inactieve subcategorieën zijn onzichtbaar voor medewerkers en niet kiesbaar voor opleiders; gekoppelde trainingen blijven gekoppeld.'
          )}
        </p>

        <Card className="p-4 mb-6">
          <h2 className="font-semibold text-gray-900 mb-3">{tr('addTitle', 'Subcategorie toevoegen')}</h2>
          <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-[220px_90px_1fr_180px_auto] gap-3 items-end">
            <div className="w-full">
              <label htmlFor="academy-sub-hoofdgroep" className="block text-sm font-medium text-gray-700 mb-1">
                {tr('groupLabel', 'Hoofdgroep')}
              </label>
              <select
                id="academy-sub-hoofdgroep"
                value={hoofdgroep}
                onChange={(e) => setHoofdgroep(e.target.value as AcademyCategorie)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {HOOFDGROEPEN.map((h) => (
                  <option key={h} value={h}>
                    {HOOFDGROEP_DEFAULTS[h].emoji} {groepLabel(h)}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label={tr('emojiLabel', 'Emoji')}
              placeholder="🧘"
              value={emoji}
              onChange={(e) => setEmoji(e.target.value)}
              maxLength={16}
            />
            <Input
              label={tr('nameLabel', 'Naam')}
              placeholder={tr('namePlaceholder', 'bv. Mindfulness & Meditatie')}
              value={naam}
              onChange={(e) => setNaam(e.target.value)}
              maxLength={100}
              required
            />
            <Input
              label={tr('slugLabel', 'Slug (optioneel)')}
              placeholder={tr('slugPlaceholder', 'automatisch uit naam')}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              maxLength={60}
            />
            <Button type="submit" disabled={busy || !naam.trim()} className="flex items-center">
              <Plus className="w-4 h-4 mr-1" /> {tr('addButton', 'Toevoegen')}
            </Button>
          </form>
        </Card>

        {isLoading && <div className="p-4 text-sm text-gray-500">…</div>}
        {isError && <div className="p-4 text-sm text-red-600">{tr('loadError', 'Subcategorieën konden niet worden geladen.')}</div>}

        {!isLoading &&
          !isError &&
          perGroep.map(({ hoofdgroep: h, items }) => (
            <Card key={h} className="p-0 overflow-hidden mb-6">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <span className="font-semibold text-gray-900">
                  {HOOFDGROEP_DEFAULTS[h].emoji} {groepLabel(h)}
                </span>
                <span className="text-xs text-gray-500">{tr('count', `${items.length} subcategorieën`, { count: items.length })}</span>
              </div>
              {items.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">{tr('empty', 'Nog geen subcategorieën in deze hoofdgroep.')}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2 w-20">{tr('colOrder', 'Volgorde')}</th>
                        <th className="px-3 py-2 w-20">{tr('colEmoji', 'Emoji')}</th>
                        <th className="px-3 py-2">{tr('colName', 'Naam')}</th>
                        <th className="px-3 py-2">{tr('colGroup', 'Hoofdgroep')}</th>
                        <th className="px-3 py-2 text-right">{tr('colTrainings', 'Trainingen')}</th>
                        <th className="px-3 py-2">{tr('colStatus', 'Status')}</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {items.map((row, index) => (
                        <SubcategorieRij
                          key={row.id}
                          row={row}
                          isFirst={index === 0}
                          isLast={index === items.length - 1}
                          busy={busy}
                          tr={tr}
                          groepLabel={groepLabel}
                          onPatch={(patch) => updateMutation.mutate({ id: row.id, patch })}
                          onMove={(richting) => verplaats(items, index, richting)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ))}
      </div>
    </div>
  );
};

interface RijProps {
  row: SubcategorieRow;
  isFirst: boolean;
  isLast: boolean;
  busy: boolean;
  tr: (key: string, defaultValue: string, opts?: Record<string, unknown>) => string;
  groepLabel: (h: AcademyCategorie) => string;
  onPatch: (patch: Patch) => void;
  onMove: (richting: -1 | 1) => void;
}

/** Eén rij met inline bewerken: naam en emoji slaan op bij blur/Enter, Escape zet terug. */
const SubcategorieRij: React.FC<RijProps> = ({ row, isFirst, isLast, busy, tr, groepLabel, onPatch, onMove }) => {
  const [naam, setNaam] = useState(row.naam);
  const [emoji, setEmoji] = useState(row.emoji ?? '');

  useEffect(() => setNaam(row.naam), [row.naam]);
  useEffect(() => setEmoji(row.emoji ?? ''), [row.emoji]);

  // Escape = annuleren: de daaropvolgende blur mag niet opslaan.
  const annuleer = useRef(false);

  const bewaarNaam = () => {
    if (annuleer.current) {
      annuleer.current = false;
      return;
    }
    const v = naam.trim();
    if (!v) return setNaam(row.naam);
    if (v !== row.naam) onPatch({ naam: v });
  };
  const bewaarEmoji = () => {
    if (annuleer.current) {
      annuleer.current = false;
      return;
    }
    const v = emoji.trim();
    if (v !== (row.emoji ?? '')) onPatch({ emoji: v || null });
  };
  const toetsen = (herstel: () => void) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') {
      herstel();
      annuleer.current = true;
      e.currentTarget.blur();
    }
  };

  const veld = 'w-full px-2 py-1 border border-transparent rounded hover:border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-transparent';

  return (
    <tr className={row.is_active ? '' : 'opacity-60'}>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={busy || isFirst}
            onClick={() => onMove(-1)}
            className="p-1 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
            aria-label={tr('moveUp', 'Omhoog')}
            title={tr('moveUp', 'Omhoog')}
          >
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            disabled={busy || isLast}
            onClick={() => onMove(1)}
            className="p-1 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
            aria-label={tr('moveDown', 'Omlaag')}
            title={tr('moveDown', 'Omlaag')}
          >
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
      <td className="px-3 py-2">
        <input
          className={`${veld} w-14 text-center text-lg`}
          value={emoji}
          maxLength={16}
          disabled={busy}
          onChange={(e) => setEmoji(e.target.value)}
          onBlur={bewaarEmoji}
          onKeyDown={toetsen(() => setEmoji(row.emoji ?? ''))}
          aria-label={tr('colEmoji', 'Emoji')}
        />
      </td>
      <td className="px-3 py-2 min-w-[220px]">
        <input
          className={`${veld} font-medium text-gray-900`}
          value={naam}
          maxLength={100}
          disabled={busy}
          onChange={(e) => setNaam(e.target.value)}
          onBlur={bewaarNaam}
          onKeyDown={toetsen(() => setNaam(row.naam))}
          aria-label={tr('colName', 'Naam')}
        />
        <div className="px-2 text-xs font-mono text-gray-400">{row.slug}</div>
      </td>
      <td className="px-3 py-2">
        <select
          value={row.hoofdgroep}
          disabled={busy}
          onChange={(e) => {
            const nieuw = e.target.value as AcademyCategorie;
            if (
              row.aantal_trainingen > 0 &&
              !window.confirm(
                tr('confirmMove', `${row.aantal_trainingen} gekoppelde training(en) verhuizen mee naar de nieuwe hoofdgroep. Doorgaan?`, {
                  count: row.aantal_trainingen,
                })
              )
            ) {
              return;
            }
            onPatch({ hoofdgroep: nieuw });
          }}
          className="px-2 py-1 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {HOOFDGROEPEN.map((h) => (
            <option key={h} value={h}>
              {groepLabel(h)}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-700">{row.aantal_trainingen}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className={`px-2 py-0.5 rounded-full text-xs ${row.is_active ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
          {row.is_active ? tr('statusActive', 'Actief') : tr('statusInactive', 'Inactief')}
        </span>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-right">
        <button
          type="button"
          disabled={busy}
          onClick={() => onPatch({ is_active: !row.is_active })}
          className="inline-flex items-center text-xs text-gray-600 hover:text-gray-900"
        >
          <Power className="w-3.5 h-3.5 mr-1" />
          {row.is_active ? tr('deactivate', 'Deactiveren') : tr('activate', 'Activeren')}
        </button>
      </td>
    </tr>
  );
};

export default AdminAcademySubcategorieenPage;
